import React, { useState, useEffect, useCallback } from 'react';
import { useAlert } from '../../components/ConfirmModal';
import { useSearchParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';

const GENRE_ICONS = {
  magical:'✨', adventure:'🗺️', funny:'😄', calm:'🌙',
  educational:'📚', cozy:'🔥', exciting:'⚡', spooky:'👻', fantasy:'🧙',
  mystery:'🔍', nature:'🌿', science:'🔬', bedtime:'🛌',
};

const AdminCommunity = () => {
  const alert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories]   = useState([]);
  const [total,   setTotal]     = useState(0);
  const [pages,   setPages]     = useState(1);
  const [loading, setLoading]   = useState(true);
  const [acting,  setActing]    = useState(null);   // id currently being updated

  const page    = parseInt(searchParams.get('page') || '1');
  const filter  = searchParams.get('filter') || 'all';   // all | featured | hidden
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const search  = searchParams.get('search') || '';

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    if (key !== 'page') p.page = '1';
    setSearchParams(p);
  };

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (filter !== 'all') params[filter] = 'true';   // featured=true or hidden=true
    if (search) params.search = search;
    API.get('/admin/community', { params })
      .then(r => { setStories(r.data.stories); setTotal(r.data.total); setPages(r.data.pages); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, filter, search]);

  useEffect(() => { load(); }, [load]);

  const handleModerate = async (id, field, value) => {
    setActing(id);
    try {
      const res = await API.patch(`/admin/community/${id}`, { [field]: value });
      setStories(prev => prev.map(s => s._id === id ? { ...s, ...res.data.story } : s));
    } catch (err) {
      await alert({ title: 'Error', message: err.response?.data?.message || 'Action failed.', danger: true });
    } finally {
      setActing(null);
    }
  };

  return (
    <AdminLayout title="Community Moderation">

      {/* Filter bar */}
      <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '14px' }}>
        <div className="d-flex flex-wrap gap-3 align-items-center">

          {/* Filter tabs */}
          <div className="d-flex gap-2">
            {[
              { value: 'all',      label: 'All Published' },
              { value: 'featured', label: '⭐ Featured'   },
              { value: 'hidden',   label: '🚫 Hidden'     },
            ].map(f => (
              <button key={f.value} type="button"
                className={`btn btn-sm rounded-pill ${filter === f.value ? 'btn-dark' : 'btn-outline-secondary'}`}
                onClick={() => set('filter', f.value)}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <form className="d-flex gap-2 ms-auto" onSubmit={e => { e.preventDefault(); set('search', searchInput); }}>
            <input
              className="form-control form-control-sm"
              style={{ width: '220px', borderRadius: '10px' }}
              placeholder="Search title / child name…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-sm btn-dark rounded-pill px-3">Search</button>
          </form>

          <span className="text-muted small">{total.toLocaleString()} stories</span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-5 text-muted">No stories found.</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-hover align-middle" style={{ fontSize: '13px' }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Title / Child</th>
                  <th>Author</th>
                  <th>Genre</th>
                  <th className="text-center">Views</th>
                  <th className="text-center">Likes</th>
                  <th>Published</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {stories.map(s => (
                  <tr key={s._id} style={{ opacity: s.isHidden ? 0.55 : 1 }}>

                    {/* Thumbnail */}
                    <td>
                      {s.illustrationUrl
                        ? <img src={s.illustrationUrl} alt="" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '6px' }} />
                        : <div className="d-flex align-items-center justify-content-center"
                            style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg,#667eea,#764ba2)', borderRadius: '6px' }}>
                            <span style={{ fontSize: '16px' }}>🌙</span>
                          </div>
                      }
                    </td>

                    {/* Title / Child */}
                    <td>
                      <div className="fw-semibold" style={{ color: 'var(--text-main)', maxWidth: '220px' }}>{s.title}</div>
                      <div className="text-muted" style={{ fontSize: '11px' }}>
                        <i className="bi bi-person-heart me-1" style={{ color: '#9b6fe8' }}></i>
                        {s.publishedChildName}
                        {s.ageGroup && <span className="ms-1">· ages {s.ageGroup}</span>}
                      </div>
                      {s.publishSlug && (
                        <Link to={`/community/${s.publishSlug}`} target="_blank" rel="noreferrer"
                          className="text-muted" style={{ fontSize: '10px' }}>/community/{s.publishSlug}</Link>
                      )}
                    </td>

                    {/* Author */}
                    <td>
                      <div style={{ fontSize: '12px' }}>{s.parent?.name || '—'}</div>
                      <div className="text-muted" style={{ fontSize: '10px' }}>{s.parent?.email}</div>
                    </td>

                    {/* Genre */}
                    <td>
                      {s.genre
                        ? <span className="badge rounded-pill" style={{ background: '#e8d5ff', color: '#6f42c1', fontSize: '10px' }}>
                            {GENRE_ICONS[s.genre] || '📖'} {s.genre}
                          </span>
                        : <span className="text-muted">—</span>}
                    </td>

                    <td className="text-center">{(s.communityViews || 0).toLocaleString()}</td>
                    <td className="text-center">{(s.communityLikes || 0).toLocaleString()}</td>

                    {/* Published date */}
                    <td style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>
                      {s.publishedAt ? new Date(s.publishedAt).toLocaleDateString() : '—'}
                    </td>

                    {/* Status badges */}
                    <td>
                      <div className="d-flex flex-column gap-1">
                        {s.isFeatured && (
                          <span className="badge rounded-pill" style={{ background: '#fef3c7', color: '#92400e', fontSize: '10px' }}>⭐ Featured</span>
                        )}
                        {s.isHidden && (
                          <span className="badge rounded-pill" style={{ background: '#fee2e2', color: '#991b1b', fontSize: '10px' }}>🚫 Hidden</span>
                        )}
                        {!s.isFeatured && !s.isHidden && (
                          <span className="badge rounded-pill" style={{ background: 'var(--header-bg)', color: 'var(--text-muted2)', fontSize: '10px' }}>Normal</span>
                        )}
                      </div>
                    </td>

                    {/* Action buttons */}
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        <button
                          className={`btn btn-xs rounded-pill px-2 py-1 ${s.isFeatured ? 'btn-warning' : 'btn-outline-warning'}`}
                          style={{ fontSize: '11px' }}
                          disabled={acting === s._id}
                          onClick={() => handleModerate(s._id, 'isFeatured', !s.isFeatured)}
                          title={s.isFeatured ? 'Remove from featured' : 'Feature this story'}
                        >
                          {acting === s._id ? <span className="spinner-border spinner-border-sm"></span> : s.isFeatured ? '★ Unfeature' : '☆ Feature'}
                        </button>
                        <button
                          className={`btn btn-xs rounded-pill px-2 py-1 ${s.isHidden ? 'btn-secondary' : 'btn-outline-danger'}`}
                          style={{ fontSize: '11px' }}
                          disabled={acting === s._id}
                          onClick={() => handleModerate(s._id, 'isHidden', !s.isHidden)}
                          title={s.isHidden ? 'Restore (unhide)' : 'Hide from community'}
                        >
                          {s.isHidden ? '👁 Restore' : '🚫 Hide'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-4">
              <button className="btn btn-outline-secondary rounded-pill" disabled={page <= 1}
                onClick={() => set('page', String(page - 1))}>
                <i className="bi bi-chevron-left"></i>
              </button>
              {[...Array(Math.min(pages, 7))].map((_, i) => {
                const p = i + 1;
                return (
                  <button key={p} onClick={() => set('page', String(p))}
                    className={`btn rounded-pill ${page === p ? 'btn-dark' : 'btn-outline-secondary'}`}>
                    {p}
                  </button>
                );
              })}
              {pages > 7 && <span className="btn btn-sm disabled">…{pages}</span>}
              <button className="btn btn-outline-secondary rounded-pill" disabled={page >= pages}
                onClick={() => set('page', String(page + 1))}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminCommunity;
